import * as React from "react";
import { Theme, darken, lighten } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { FieldLabelWithHelp } from "./FieldHelpIcon";
import {
  CalendarDate,
  dateLocalDeDia,
  diaLocalDeDate,
  isCalendarDate,
} from "../../../common/calendarDate";

import esLocale from "date-fns/locale/es";
import ptSaLocale from "date-fns/locale/pt-BR";
import enSaLocale from "date-fns/locale/en-US";

type InputDateProps = {
  label: string;
  /** Un día de calendario, "AAAA-MM-DD" (fecha sin hora), o null. */
  value: CalendarDate | null;
  helperText?: string;
  error?: boolean;
  // Aviso que no bloquea: el helperText se pinta de advertencia. Un error lo tapa.
  warning?: boolean;
  /** El día elegido, "AAAA-MM-DD"; null si quedó vacío o a medio escribir. */
  onChange: (value: CalendarDate | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  placeholder?: string;
  nullText?: string;
  description?: string;
};

/** El Date local que muestra el selector para un día: armado con sus partes; la hora no interviene. */
export const pickerValue = (
  value: CalendarDate | null | undefined
): Date | null => (isCalendarDate(value) ? dateLocalDeDia(value) : null);

/**
 * El día que la persona eligió en el selector (getters locales), o null si quedó vacío,
 * a medio escribir o fuera de los años 0001 a 9999 (un año 0 o 10000 escrito a mano).
 */
export const dayFromPicker = (date: Date | null): CalendarDate | null => {
  if (!date || Number.isNaN(date.getTime())) return null;
  const day = diaLocalDeDate(date);
  return isCalendarDate(day) ? day : null;
};

export const InputDate: React.FC<InputDateProps> = ({
  label,
  value,
  onChange,
  helperText = "",
  error = false,
  warning = false,
  autoFocus = false,
  disabled = false,
  hidden = false,
  placeholder,
  nullText,
  description,
}) => {
  const [focused, setFocused] = React.useState(false);
  // Lo que muestra el selector. Mientras la persona escribe, un día incompleto deja el
  // valor de afuera en null y esto no se rehace (no se le borra lo que escribe); un
  // valor de afuera nuevo (otro registro, un reset) sí lo rehace.
  const [shown, setShown] = React.useState<Date | null>(() =>
    pickerValue(value)
  );
  React.useEffect(() => {
    setShown((current) =>
      dayFromPicker(current) === (value ?? null) ? current : pickerValue(value)
    );
  }, [value]);
  if (hidden) return null;
  const locale = {
    es: esLocale,
    pt: ptSaLocale,
    en: enSaLocale,
  };

  const isEmpty = value === null || value === undefined;
  const showLabelInside = description && isEmpty && !focused;
  const baseLabel =
    nullText && isEmpty && !focused ? `${label} (${nullText})` : label;
  const displayLabel = showLabelInside ? undefined : baseLabel;
  const effectivePlaceholder = showLabelInside
    ? nullText
      ? `${label} (${nullText})`
      : label
    : placeholder;

  return (
    <LocalizationProvider
      adapterLocale={locale["es"]}
      dateAdapter={AdapterDateFns}
    >
      <div style={{ display: "flex" }}>
        <DatePicker
          disabled={disabled}
          label={
            <FieldLabelWithHelp
              label={displayLabel}
              description={description}
            />
          }
          value={shown}
          onChange={(date: Date | null) => {
            setShown(date);
            onChange(dayFromPicker(date));
          }}
          slotProps={{
            textField: {
              autoFocus: autoFocus,
              size: "small",
              sx: { m: 1 },
              helperText: helperText,
              error: error,
              FormHelperTextProps:
                warning && !error
                  ? {
                      sx: {
                        color: (theme: Theme) =>
                          theme.palette.mode === "dark"
                            ? lighten(theme.palette.warning.main, 0.5)
                            : darken(theme.palette.warning.main, 0.5),
                      },
                    }
                  : undefined,
              placeholder: effectivePlaceholder,
              onFocus: () => setFocused(true),
              onBlur: () => setFocused(false),
              InputLabelProps: description ? { shrink: true } : undefined,
            },
          }}
        />
      </div>
    </LocalizationProvider>
  );
};
